import React, { forwardRef, useEffect, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { defaultPfpUrl } from "../utils";
import { cn } from "../../lib/utils";

/**
 * Two-person silhouette for the @all row.
 * @returns {JSX.Element}
 */
function GroupIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 text-text-muted"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden
    >
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.6-3 2.7-5 5.5-5s4.9 2 5.5 5" />
      <circle cx="16.5" cy="9" r="2.4" />
      <path d="M15 14.2c2.3.4 4 2.2 4.5 4.8" />
    </svg>
  );
}

/**
 * Floating @mention candidate list above a composer.
 *
 * @param {Object} props
 * @param {Array<object>} props.candidates
 * @param {number} props.highlightedIndex
 * @param {(candidate: object) => void} props.onSelect
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 * @returns {JSX.Element | null}
 */
const MentionPicker = forwardRef(function MentionPicker(
  { candidates, highlightedIndex, onSelect, className, style },
  forwardedRef
) {
  const listRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const active = list.querySelector('[data-mention-active="true"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const setRefs = (node) => {
    listRef.current = node;
    if (typeof forwardedRef === "function") {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  const positionClass =
    "fixed z-[60] box-border w-max min-w-[12rem] max-w-[min(24rem,calc(100vw-1rem))] rounded-xl border border-ui-border bg-ui-card shadow-shell-md";

  if (!Array.isArray(candidates) || candidates.length === 0) {
    return (
      <div
        ref={setRefs}
        role="listbox"
        aria-label="Mention suggestions"
        style={style}
        className={cn(positionClass, "overflow-hidden py-3", className)}
      >
        <p className="px-3 text-sm text-text-muted">No matches</p>
      </div>
    );
  }

  return (
    <div
      ref={setRefs}
      role="listbox"
      aria-label="Mention suggestions"
      style={style}
      className={cn(
        positionClass,
        "max-h-64 overflow-y-auto py-1 list-scrollbar",
        className
      )}
    >
      {candidates.map((candidate, index) => {
        const selected = index === highlightedIndex;
        const label = candidate.name || "User";
        return (
          <button
            key={`${candidate.is_all ? "all" : candidate.is_org ? "org" : "user"}-${candidate.id}-${index}`}
            type="button"
            role="option"
            aria-selected={selected}
            data-mention-active={selected ? "true" : undefined}
            className={cn(
              "flex w-full items-center gap-3 px-3 py-2 text-left",
              selected ? "bg-highlight" : "hover:bg-secondary"
            )}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect(candidate);
            }}
          >
            {candidate.is_all ? (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary">
                <GroupIcon />
              </span>
            ) : (
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={candidate.pfp || defaultPfpUrl} alt="" />
                <AvatarFallback>{label.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-text-primary">
                {label}
              </span>
              {candidate.subtitle ? (
                <span className="block truncate text-xs text-text-muted">
                  {candidate.subtitle}
                </span>
              ) : candidate.username ? (
                <span className="block truncate text-xs text-text-muted">
                  @{candidate.username}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
});

MentionPicker.displayName = "MentionPicker";

export default MentionPicker;
